"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSuspenseContext = void 0;
const react_1 = require("react");
const react_use_1 = require("react-use");
const useMutateObserver_1 = require("../Popover/useMutateObserver");
const useDeepMemo_1 = require("../useDeepMemo");
function useSuspenseContext(props, containerRef) {
    var _a, _b;
    const [spinnerInView, setSpinnerInView] = react_1.useState(false);
    const [scrolledTopAt, setScrolledTopAt] = react_1.useState(0);
    const status = (_a = props.status) !== null && _a !== void 0 ? _a : props.hook.status;
    const contextValue = useDeepMemo_1.useDeepMemo({
        containerRef,
        hook: props.hook,
        status,
        spinnerInView,
        setSpinnerInView,
        dispatch: () => { },
        animated: false,
    });
    const fromTop = ((_b = props.infiniteScroll) === null || _b === void 0 ? void 0 : _b.position) === 'top';
    const prevScrollPosRef = react_1.useRef({
        top: 0,
        height: 0,
    });
    /* only do scroll adjustments if we know confidentially its as a result of a new page from scrolling.
    the app could be appending rows manually. */
    const adjustScrollPosRef = react_1.useRef({
        scrollToBottom: () => { },
        scrollToTop: () => { },
    });
    adjustScrollPosRef.current = {
        scrollToTop: () => {
            var _a;
            if (fromTop && containerRef.current) {
                let heightDifference = containerRef.current.scrollHeight - prevScrollPosRef.current.height;
                if (!containerRef.current.scrollHeight || heightDifference <= 0) {
                    // heightDifference = spinnerRef.current?.getBoundingClientRect().height ?? 80
                    heightDifference = 80;
                }
                (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ top: prevScrollPosRef.current.top + heightDifference });
                setScrolledTopAt(Date.now());
            }
        },
        scrollToBottom: () => {
            var _a;
            (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ top: containerRef.current.scrollHeight });
        },
    };
    const paginator = props.hook;
    const { setTargetPage, lastFetchedPage } = paginator;
    const [canInfiniteScroll, setCanInfiniteScroll] = react_1.useState(false);
    react_1.useEffect(() => {
        if (canInfiniteScroll && status === 'loading') {
            setCanInfiniteScroll(false);
        }
    }, [canInfiniteScroll, lastFetchedPage, status]);
    react_1.useEffect(() => {
        setCanInfiniteScroll(false);
        const t = setTimeout(() => {
            setCanInfiniteScroll(true);
        }, 500);
        return () => clearTimeout(t);
    }, [paginator.data]);
    react_1.useEffect(() => {
        var _a, _b, _c, _d;
        if (spinnerInView && canInfiniteScroll && status !== 'loading') {
            prevScrollPosRef.current = {
                top: (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) !== null && _b !== void 0 ? _b : 0,
                height: (_d = (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.scrollHeight) !== null && _d !== void 0 ? _d : 0,
            };
            /* Must immediately set to false to prevent spam call. */
            setCanInfiniteScroll(false);
            setTargetPage((page) => page + 1);
        }
    }, [canInfiniteScroll, containerRef, setCanInfiniteScroll, setTargetPage, spinnerInView, status]);
    useMutateObserver_1.useMutateObserver(containerRef.current, () => {
        const curTime = Date.now();
        // think of images loading after the initial scroll to bottom which will change the size
        // we need to rescroll to the bottom and reset the watcher incase it changes again
        if (scrollBottomWatchAt && ((curTime - 1000) < scrollBottomWatchAt)) {
            setScrollBottomWatchAt(curTime);
            adjustScrollPosRef.current.scrollToBottom();
        }
    });
    const [scrollBottomWatchAt, setScrollBottomWatchAt] = react_1.useState(0);
    const previousPage = react_use_1.usePrevious(paginator.lastFetchedPage);
    react_1.useEffect(() => {
        /* Initial scroll bottom */
        if (paginator.data && fromTop && paginator.lastFetchedPage === 1) {
            adjustScrollPosRef.current.scrollToBottom();
            setScrollBottomWatchAt(Date.now());
        }
    }, [previousPage, fromTop, paginator.data, paginator.lastFetchedPage]);
    react_1.useEffect(() => {
        if (fromTop && scrolledTopAt < (Date.now() - 1000) && paginator.lastFetchedPage === 1) {
            adjustScrollPosRef.current.scrollToBottom();
        }
    }, [paginator.data, fromTop, scrolledTopAt, paginator.lastFetchedPage]);
    react_1.useEffect(() => {
        if (fromTop && paginator.lastFetchedPage) {
            adjustScrollPosRef.current.scrollToTop();
        }
    }, [fromTop, paginator.lastFetchedPage]);
    return contextValue;
}
exports.useSuspenseContext = useSuspenseContext;
